import { apiClient } from './api';

// Platform capability probes for web push + installability.
// On iOS, push is only available once the site is installed to the home
// screen (iOS 16.4+); in a plain Safari tab PushManager is absent.

export function isIos(): boolean {
  const ua = navigator.userAgent;
  const isAppleMobile = /iPhone|iPad|iPod/i.test(ua);
  // iPadOS 13+ reports as Mac; detect via touch support
  const isIpadOs = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return isAppleMobile || isIpadOs;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// iOS Safari tab: push would work, but only after installing to home screen
export function pushRequiresInstall(): boolean {
  return isIos() && !isStandalone();
}

export function notificationPermission(): NotificationPermission | null {
  return 'Notification' in window ? Notification.permission : null;
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      console.warn('Service worker registration failed:', error);
    });
  });
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

async function getExistingSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

// Requests permission (must be called from a user gesture), subscribes the
// browser, and registers the subscription with the backend for this player.
export async function subscribeToPush(gameKey: string): Promise<void> {
  if (!pushSupported()) {
    throw new Error('Push notifications are not supported on this device');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted');
  }

  const { data } = await apiClient.getVapidPublicKey();
  const registration = await navigator.serviceWorker.ready;

  const subscription =
    (await registration.pushManager.getSubscription()) ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    }));

  await registerWithBackend(gameKey, subscription);
}

// Each game gives the player a new player record, so a browser that is
// already subscribed must be registered again for every game it joins;
// otherwise notifications only ever reach the first game's player.
export async function syncExistingSubscription(gameKey: string): Promise<boolean> {
  if (!pushSupported() || notificationPermission() !== 'granted') return false;

  const subscription = await getExistingSubscription();
  if (!subscription) return false;

  await registerWithBackend(gameKey, subscription);
  return true;
}

async function registerWithBackend(gameKey: string, subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Browser returned an incomplete push subscription');
  }

  await apiClient.savePushSubscription(gameKey, {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
}

export async function unsubscribeFromPush(gameKey: string): Promise<void> {
  if (!pushSupported()) return;

  const subscription = await getExistingSubscription();
  if (!subscription) return;

  try {
    await apiClient.deletePushSubscription(gameKey, subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
}
